import { useCallback, useMemo, useState } from "react";
import type * as React from "react";
import ArrowForward from "@mui/icons-material/ArrowForward";
import { Box, Typography } from "@mui/material";
import axios, { AxiosError } from "axios";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { postMembershipInvites, type MembershipInvite } from "apis/v3/authApi";
import GLEmailField, {
  normalizeEmailInput
} from "components/v3/forms/formComponents/GLEmailField";
import GLTextField from "components/v3/forms/formComponents/GLTextField";
import GradientFramedLayout from "components/v3/layouts/GradientFramedLayout";
import AccountExitLink from "components/v3/sharedComponents/AccountExitLink";
import GLButton from "components/v3/sharedComponents/GLButton";
import { COLORS } from "components/v3/Theme/colors";
import MembershipInviteHowItWorksBlock from "components/v3/pages/account/membershipInvite/MembershipInviteHowItWorksBlock";
import type { MembershipInviteHowItWorksStep } from "components/v3/pages/account/membershipInvite/MembershipInviteHowItWorksBlock";
import ShareGenomicLifeInviteSentModal from "components/v3/pages/account/membershipInvite/ShareGenomicLifeInviteSentModal";
import GiftMembershipStepAccessIcon from "assets/images/gift-membership-step-access.svg";
import GiftMembershipStepEnvelopeIcon from "assets/images/gift-membership-step-envelope.svg";
import GiftMembershipStepPencilIcon from "assets/images/gift-membership-step-pencil.svg";
import { GIFT_MAX_PLAN_QUANTITY } from "constants/membershipPriceConstants";
import { PATHS } from "constants/pathConstants";
import {
  trackMembershipInviteCompleted,
  trackMembershipInviteFailed
} from "analytics";
import { useMemberOwnedEmailValidation } from "hooks/useMemberOwnedEmailValidation";
import PageTitle from "utils/PageTitle";
import { openGLSnackbar } from "store/reducers/snackbar/openGLSnackbarSlice";
import { validateEmailString } from "utils/utils";

const formCardSx = {
  bgcolor: COLORS.WHITE,
  borderRadius: "12px",
  boxSizing: "border-box" as const,
  boxShadow: "0 10px 28px rgba(17, 24, 39, 0.07)",
  flex: { md: "1 1 0", xs: "none" },
  minWidth: 0,
  p: { md: 3, xs: 2.5 },
  width: "100%"
} as const;

const sidebarSx = {
  boxSizing: "border-box" as const,
  flex: { md: "0 0 340px", xs: "none" },
  minWidth: 0,
  p: { md: 3, xs: 2.5 },
  width: { md: "auto", xs: "100%" }
} as const;

const sidebarHowItWorksSteps: ReadonlyArray<MembershipInviteHowItWorksStep> = [
  {
    icon: GiftMembershipStepEnvelopeIcon,
    title: "Send them an invite",
    body: "They will be sent an email with an invite code."
  },
  {
    icon: GiftMembershipStepPencilIcon,
    title: "They sign up",
    body: "They'll need to create an account to use the code you sent them."
  },
  {
    icon: GiftMembershipStepAccessIcon,
    title: "They become a member",
    body: "After purchasing a plan, they'll get full access to Genomic Life."
  }
];

function extractInviteApiErrorMessage(response: AxiosError): string {
  const data = response.response?.data as
    | { meta?: { message?: string } }
    | undefined;
  const msg = data?.meta?.message;
  return typeof msg === "string" && msg.trim() !== ""
    ? msg
    : "Something went wrong, please try again.";
}

const ShareGenomicLifeInvitePage: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const {
    isRecipientEmailOwnedByMember,
    validateRecipientEmail
  } = useMemberOwnedEmailValidation();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sentModalOpen, setSentModalOpen] = useState(false);
  const [sentInviteeFirstName, setSentInviteeFirstName] = useState("");
  const [inviteFormKey, setInviteFormKey] = useState(0);
  const [invitesSentCount, setInvitesSentCount] = useState(0);

  const atInviteLimit = invitesSentCount >= GIFT_MAX_PLAN_QUANTITY;

  const emailNormalized = useMemo(() => normalizeEmailInput(email), [email]);
  const confirmNormalized = useMemo(
    () => normalizeEmailInput(confirmEmail),
    [confirmEmail]
  );

  const canSubmit = useMemo(() => {
    if (
      firstName.trim().length < 1 ||
      lastName.trim().length < 1 ||
      !validateEmailString(emailNormalized) ||
      isRecipientEmailOwnedByMember(emailNormalized)
    ) {
      return false;
    }
    return (
      confirmNormalized.length > 0 && confirmNormalized === emailNormalized
    );
  }, [
    confirmNormalized,
    emailNormalized,
    firstName,
    isRecipientEmailOwnedByMember,
    lastName
  ]);

  const handleInviteSentExit = useCallback(() => {
    setSentModalOpen(false);
    history.push(PATHS.ACCOUNT_MANAGEMENT_PAYMENT_INFORMATION);
  }, [history]);

  const handleSendAnotherInvite = useCallback(() => {
    if (atInviteLimit) return;
    setSentModalOpen(false);
    setFirstName("");
    setLastName("");
    setEmail("");
    setConfirmEmail("");
    setInviteFormKey((key) => key + 1);
  }, [atInviteLimit]);

  const handleSubmit = useCallback(() => {
    if (!canSubmit || submitting || atInviteLimit) return;
    const invite: MembershipInvite = {
      email: emailNormalized,
      firstName: firstName.trim(),
      lastName: lastName.trim()
    };
    setSubmitting(true);
    postMembershipInvites({ invites: [invite] }).then((response) => {
      if (axios.isAxiosError(response)) {
        trackMembershipInviteFailed(response);
        dispatch(
          openGLSnackbar({
            variant: "error",
            header: "",
            subText: extractInviteApiErrorMessage(response)
          })
        );
        setSubmitting(false);
        return;
      }
      const invites =
        response &&
        "data" in response &&
        response.data?.data &&
        "invites" in response.data.data
          ? response.data.data.invites
          : null;
      if (invites && invites.length > 0) {
        const displayFirst =
          invites[0].firstName?.trim() || invite.firstName.trim();
        trackMembershipInviteCompleted({ inviteCount: invites.length });
        setSentInviteeFirstName(displayFirst);
        setInvitesSentCount((prev) => Math.max(prev + 1, invites.length));
        setSentModalOpen(true);
        setSubmitting(false);
        return;
      }
      trackMembershipInviteFailed();
      dispatch(
        openGLSnackbar({
          variant: "error",
          header: "",
          subText: "Something went wrong, please try again."
        })
      );
      setSubmitting(false);
    });
  }, [
    atInviteLimit,
    canSubmit,
    dispatch,
    emailNormalized,
    firstName,
    lastName,
    submitting
  ]);

  PageTitle("Invite to Genomic Life");

  return (
    <>
      <GradientFramedLayout showHeader={false} showFooter={false}>
        <Box
          sx={{
            alignSelf: "stretch",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            gap: { md: 12, xs: 7 },
            marginX: "auto",
            maxWidth: { lg: 1140, md: "100%", xs: "100%" },
            minWidth: 0,
            pb: { md: 5, xs: 4 },
            pt: { md: 4, xs: 3 },
            px: { md: 3, xs: 2 },
            width: "100%"
          }}
        >
          <AccountExitLink
            to={PATHS.ACCOUNT_MANAGEMENT_PAYMENT_INFORMATION}
            sx={{ alignSelf: "flex-start" }}
          />

          <Box
            sx={{
              alignItems: "stretch",
              display: "flex",
              flexDirection: { lg: "row", xs: "column" },
              gap: 3,
              width: "100%"
            }}
          >
            <Box sx={formCardSx}>
              <Typography
                variant="h5_primary"
                sx={{
                  lineHeight: 1.3,
                  mb: 1.5
                }}
              >
                Who would you like to invite to Genomic Life?
              </Typography>
              <Typography
                component="p"
                variant="body"
                sx={{
                  color: COLORS.DARK_GRAY_2,
                  lineHeight: 1.6,
                  m: 0,
                  mb: 2.5
                }}
              >
                Once you enter the information below, we will send an invitation
                to join Genomic Life. The email will include information about
                our program and the ability to purchase their plan.
              </Typography>

              <Box
                key={inviteFormKey}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  width: "100%"
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    gridTemplateColumns: { sm: "1fr 1fr", xs: "1fr" },
                    mb: 2
                  }}
                >
                  <GLTextField
                    id="inviteFirstName"
                    label="First Name"
                    required
                    value={firstName}
                    onChange={({ e }) => setFirstName(e.target.value)}
                  />
                  <GLTextField
                    id="inviteLastName"
                    label="Last Name"
                    required
                    value={lastName}
                    onChange={({ e }) => setLastName(e.target.value)}
                  />
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <GLEmailField
                    id="inviteEmail"
                    label="Email Address"
                    required
                    value={email}
                    onChange={({ e }) => setEmail(e.target.value)}
                    placeholder="name@example.com"
                    additionalValidation={validateRecipientEmail}
                  />
                  <GLEmailField
                    id="inviteConfirmEmail"
                    label="Confirm email address"
                    required
                    value={confirmEmail}
                    onChange={({ e }) => setConfirmEmail(e.target.value)}
                    placeholder="name@example.com"
                    additionalValidation={(normalized) =>
                      normalized.length > 0 &&
                      normalizeEmailInput(email) !== normalized
                        ? "Email addresses do not match"
                        : undefined
                    }
                  />
                </Box>

                <Typography
                  component="p"
                  variant="body_dark_gray"
                  sx={{
                    mt: 2,
                    mb: 3
                  }}
                >
                  Please note that Genomic Life can only serve people 18 years
                  and older who are located in the United States. Age
                  verification is required upon sign up.
                </Typography>

                <Box
                  sx={{
                    alignItems: { sm: "flex-start", xs: "stretch" },
                    display: "flex",
                    flexDirection: "column",
                    gap: 1
                  }}
                >
                  <GLButton
                    buttonType="primary"
                    disabled={!canSubmit || atInviteLimit}
                    loading={submitting}
                    onClick={handleSubmit}
                    rightIcon={<ArrowForward />}
                    text="Send Invite"
                    sx={{
                      alignSelf: { sm: "flex-start", xs: "stretch" },
                      maxWidth: { sm: "max-content", xs: "100%" },
                      width: { sm: "fit-content", xs: "100%" }
                    }}
                  />
                </Box>
              </Box>
            </Box>

            <Box sx={sidebarSx}>
              <Typography
                component="h2"
                sx={{
                  fontSize: "20px",
                  fontWeight: 700,
                  mb: 0.5
                }}
              >
                How it works
              </Typography>
              <Typography
                component="p"
                variant="body"
                sx={{ color: COLORS.DARK_GRAY_2, m: 0, mb: 3.5 }}
              >
                Inviting someone to Genomic Life is as easy as 1-2-3:
              </Typography>

              <MembershipInviteHowItWorksBlock steps={sidebarHowItWorksSteps} />
            </Box>
          </Box>
        </Box>
      </GradientFramedLayout>
      <ShareGenomicLifeInviteSentModal
        inviteLimitReached={atInviteLimit}
        inviteeFirstName={sentInviteeFirstName}
        open={sentModalOpen}
        onExit={handleInviteSentExit}
        onSendAnother={handleSendAnotherInvite}
      />
    </>
  );
};

export default ShareGenomicLifeInvitePage;
